import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { http, waLink, REPAIR_SERVICES, apiError } from "@/lib/shop";
import { WhatsAppIcon } from "./WhatsAppIcon";

const empty = { name: "", phone: "", device: "", service: "", notes: "" };

export const RepairDialog = ({ open, onOpenChange, initialService }) => {
  const [form, setForm] = useState(empty);
  const [date, setDate] = useState();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, service: initialService || f.service }));
  }, [open, initialService]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) return toast.error("Please choose the repair you need.");
    setBusy(true);
    const preferred = date ? format(date, "EEE d MMM yyyy") : null;
    const win = window.open("", "_blank");
    try {
      await http.post("/repairs", { ...form, preferred_date: preferred, notes: form.notes || null });
      const msg = `Hi FONZ FIX4U, I'd like to book a repair.\nName: ${form.name}\nPhone: ${form.phone}\nDevice: ${form.device}\nRepair: ${form.service}${preferred ? `\nPreferred day: ${preferred}` : ""}${form.notes ? `\nDetails: ${form.notes}` : ""}`;
      if (win) win.location.href = waLink(msg);
      toast.success("Repair request sent — finish the chat on WhatsApp and we'll confirm.");
      setForm(empty);
      setDate(undefined);
      onOpenChange(false);
    } catch (err) {
      if (win) win.close();
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="repair-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black">Book a Repair</DialogTitle>
          <DialogDescription>Tell us what's wrong. We'll open WhatsApp with your details so you can send them straight to us.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name"><Input required value={form.name} onChange={set("name")} data-testid="repair-name-input" /></Field>
            <Field label="Phone number"><Input required type="tel" value={form.phone} onChange={set("phone")} data-testid="repair-phone-input" /></Field>
          </div>
          <Field label="Device (e.g. iPhone 13, Galaxy S22)"><Input required value={form.device} onChange={set("device")} data-testid="repair-device-input" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Repair needed">
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger data-testid="repair-service-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>
                  {REPAIR_SERVICES.map((s) => <SelectItem key={s} value={s} data-testid={`repair-service-option-${s.split(" ")[0].toLowerCase()}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Preferred day (optional)">
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="flex h-9 w-full items-center gap-2 rounded-md border border-input px-3 text-sm text-left" data-testid="repair-date-trigger">
                    <CalendarIcon className="h-4 w-4 text-slate-500" />
                    {date ? format(date, "EEE d MMM") : <span className="text-slate-500">Pick a day</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }} initialFocus />
                </PopoverContent>
              </Popover>
            </Field>
          </div>
          <Field label="What's the problem? (optional)"><Textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="repair-notes-input" /></Field>
          <button type="submit" disabled={busy} className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 font-bold text-white hover:bg-[#20BD5A] disabled:opacity-60 transition-colors" data-testid="repair-submit-btn">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <WhatsAppIcon />} Send repair request
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="grid gap-1.5"><Label className="text-sm font-semibold text-slate-700">{label}</Label>{children}</div>
);
